import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getGraphCaseConnections, getCaseNetwork, deleteGraphEdge } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { NetworkGraph } from '@/components/NetworkGraph';
import { MiniCaseGraph } from '@/components/MiniCaseGraph';
import { AddConnectionModal } from '@/components/AddConnectionModal';
import { BatchClusterConnectModal } from '@/components/BatchClusterConnectModal';
import { toast } from 'sonner';
import {
  Network, Users, Car, MapPin, AlertTriangle, ChevronDown, ChevronRight,
  ExternalLink, Loader2, Link2, Eye, Calendar, Plus, Trash2, Layers,
} from 'lucide-react';

// Confidence badge colors
const CONFIDENCE_STYLES = {
  confirmed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-300',
  probable: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-300',
  possible: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-300',
};

// Edge type display labels
const EDGE_LABELS = {
  missing_with: 'Missing Together',
  family_relationship: 'Family',
  same_suspect: 'Shared Suspect',
  same_vehicle: 'Same Vehicle',
  same_location: 'Same Location',
  same_event: 'Shared Event',
  same_dump_site: 'Same Dump Site',
  reported_possible_link: 'Reported Link',
  investigative_lead: 'Investigative Lead',
  sighting_reported_at: 'Sighting',
  searched_at: 'Searched At',
  found_at: 'Found At',
  last_seen_at: 'Last Seen At',
  potential_match: 'Potential Match',
};

const STATUS_STYLES = {
  approved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  pending_review: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const ConnectionItem = ({ connection, isAdmin, onRemove }) => {
  const confidence = connection.confidence || 'possible';
  const edgeLabel = EDGE_LABELS[connection.edge_type] || connection.edge_type;
  const status = connection.edge_status || 'pending_review';
  const investigatorLabel = connection.edge_label || connection.relationship;

  return (
    <div className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
         data-testid={`case-conn-row-${connection.edge_id || connection.node_id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex-1 min-w-0">
          {connection.case_id ? (
            <Link
              to={`/case/${connection.case_id}`}
              className="font-medium text-sm truncate hover:text-cyan-700 dark:hover:text-cyan-300 hover:underline"
            >
              {connection.label}
            </Link>
          ) : (
            <div className="font-medium text-sm truncate">{connection.label}</div>
          )}
          {investigatorLabel && (
            <div className="text-xs text-muted-foreground truncate" title={investigatorLabel}>
              {investigatorLabel}
            </div>
          )}
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <Badge variant="outline" className="text-[10px] py-0 h-4">
              {edgeLabel}
            </Badge>
            {isAdmin && (
              <Badge variant="secondary" className={`text-[10px] py-0 h-4 border-0 ${STATUS_STYLES[status]}`}>
                {status.replace('_', ' ')}
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Badge variant="outline" className={`text-xs ${CONFIDENCE_STYLES[confidence]}`}>
          {confidence}
        </Badge>
        {connection.case_id && (
          <Link to={`/case/${connection.case_id}`}>
            <Button variant="ghost" size="sm" className="h-7 px-2"
                    data-testid={`case-conn-view-${connection.node_id}`}>
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
          </Link>
        )}
        {isAdmin && connection.edge_id && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
            onClick={() => onRemove(connection)}
            title="Remove connection"
            data-testid={`case-conn-remove-${connection.edge_id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, count, color = "blue" }) => {
  const colors = {
    blue: "text-blue-600 dark:text-blue-400",
    purple: "text-purple-600 dark:text-purple-400",
    amber: "text-amber-600 dark:text-amber-400",
    green: "text-green-600 dark:text-green-400",
    red: "text-red-600 dark:text-red-400",
  };
  return (
    <div className="flex items-center justify-between mb-2">
      <div className={`flex items-center gap-2 font-medium text-sm ${colors[color]}`}>
        <Icon className="w-4 h-4" />
        {title}
      </div>
      {count > 0 && (
        <Badge variant="secondary" className="text-xs">{count}</Badge>
      )}
    </div>
  );
};

const CaseNetworkSection = ({ caseId, caseName, caseType }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.is_admin;

  const [connections, setConnections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showGraphDialog, setShowGraphDialog] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);

  // Mini inline graph
  const [miniGraphData, setMiniGraphData] = useState(null);
  const [miniGraphLoading, setMiniGraphLoading] = useState(false);

  // Admin modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showClusterModal, setShowClusterModal] = useState(false);
  const [removingEdgeId, setRemovingEdgeId] = useState(null);

  const loadConnections = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const data = await getGraphCaseConnections(caseId);
      setConnections(data);
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Error loading case connections:', err);
      }
      setConnections({ has_connections: false });
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  const loadMiniGraph = useCallback(async () => {
    if (!caseId) return;
    setMiniGraphLoading(true);
    try {
      const data = await getCaseNetwork(caseId, {
        depth: 1, include_vehicles: true, include_locations: true,
        include_events: false, include_poi: false,
      });
      setMiniGraphData(data);
    } catch (err) {
      // 404 = no node for this case yet; just leave the mini-graph empty
      if (err.response?.status !== 404) {
        console.error('mini-graph fetch failed', err);
      }
      setMiniGraphData(null);
    } finally {
      setMiniGraphLoading(false);
    }
  }, [caseId]);

  useEffect(() => { loadConnections(); }, [loadConnections]);
  useEffect(() => { loadMiniGraph(); }, [loadMiniGraph]);

  const handleOpenFullGraph = async () => {
    setShowGraphDialog(true);
    setGraphLoading(true);
    try {
      const data = await getCaseNetwork(caseId, {
        depth: 2, include_vehicles: true, include_locations: true,
        include_events: true, include_poi: isAdmin,
      });
      setGraphData(data);
    } catch (err) {
      console.error('full network fetch failed', err);
    } finally {
      setGraphLoading(false);
    }
  };

  const handleRemove = async (conn) => {
    if (!conn.edge_id) return;
    if (!window.confirm(`Remove "${EDGE_LABELS[conn.edge_type] || conn.edge_type}" link to ${conn.label}?`)) return;
    setRemovingEdgeId(conn.edge_id);
    try {
      await deleteGraphEdge(conn.edge_id);
      toast.success('Connection removed');
      await Promise.all([loadConnections(), loadMiniGraph()]);
    } catch (e) {
      toast.error(`Remove failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRemovingEdgeId(null);
    }
  };

  const handleConnectionSaved = async () => {
    await Promise.all([loadConnections(), loadMiniGraph()]);
  };

  // Click a mini-graph node to navigate to that case (if it has one).
  // Accepts both the raw `case_id` (from /graph/case/.../network response) and
  // the remapped `caseId` (from the full NetworkGraph's processedData).
  const handleMiniNodeClick = (node) => {
    const target = node?.caseId || node?.case_id;
    if (target && target !== caseId) {
      window.location.href = `/case/${target}`;
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <Card className="border-cyan-200 dark:border-cyan-800">
        <CardContent className="py-6">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading network connections...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Public visitor with no connections — render nothing
  if (!connections?.has_connections && !isAdmin) return null;

  const linkedCases = connections?.linked_cases || [];
  const vehicles = connections?.vehicles || [];
  const locations = connections?.locations || [];
  const poi = connections?.poi || [];
  const events = connections?.events || [];
  const totalApproved = linkedCases.length + vehicles.length + locations.length
    + (isAdmin ? poi.length : 0) + events.length;

  // Header with admin action buttons (shared by empty + populated states)
  const cardHeader = (
    <CardHeader className="bg-gradient-to-r from-cyan-50 to-white dark:from-cyan-950/30 dark:to-slate-900 py-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <CardTitle className="font-heading flex items-center gap-2 text-cyan-700 dark:text-cyan-400 text-base">
          <Network className="w-5 h-5" />
          Case Network
          {totalApproved > 0 && (
            <Badge className="ml-2 bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300 border-0">
              {totalApproved} connection{totalApproved !== 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-1.5 flex-wrap">
          {isAdmin && (
            <>
              <Button
                variant="default"
                size="sm"
                onClick={() => setShowAddModal(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white h-8"
                data-testid="case-network-add-connection-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Connection
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowClusterModal(true)}
                className="text-cyan-700 border-cyan-400 hover:bg-cyan-50 dark:text-cyan-300 dark:border-cyan-700 dark:hover:bg-cyan-950/40 h-8"
                data-testid="case-network-cluster-btn"
              >
                <Layers className="w-4 h-4 mr-1" />
                Connect to Cluster
              </Button>
            </>
          )}
          {totalApproved > 0 && (
            <Dialog open={showGraphDialog} onOpenChange={setShowGraphDialog}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenFullGraph}
                  className="text-cyan-600 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:bg-cyan-950/50 h-8"
                  data-testid="case-network-full-graph-btn"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Full Network
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Network className="w-5 h-5" />
                    Network Graph: {caseName}
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                  <NetworkGraph
                    graphData={graphData}
                    loading={graphLoading}
                    centerNodeId={graphData?.center_node_id}
                    height={500}
                    showControls={true}
                    showLegend={true}
                    onNodeClick={handleMiniNodeClick}
                  />
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </CardHeader>
  );

  // Empty state for admins
  if (!connections?.has_connections) {
    return (
      <>
        <Card className="border-cyan-200 dark:border-cyan-800" data-testid="case-network-section">
          {cardHeader}
          <CardContent className="py-6">
            <div className="text-center text-muted-foreground text-sm">
              <Network className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No network connections yet for this case.</p>
              <p className="text-xs mt-1 opacity-70">
                Add one manually with <strong>Add Connection</strong>, or batch-link to a known cluster.
              </p>
            </div>
          </CardContent>
        </Card>
        <AddConnectionModal
          open={showAddModal}
          onOpenChange={setShowAddModal}
          sourceCaseId={caseId}
          sourceCaseName={caseName}
          sourceCaseType={caseType}
          onSaved={handleConnectionSaved}
        />
        <BatchClusterConnectModal
          open={showClusterModal}
          onOpenChange={setShowClusterModal}
          sourceCaseId={caseId}
          sourceCaseName={caseName}
          sourceCaseType={caseType}
          onSaved={handleConnectionSaved}
        />
      </>
    );
  }

  // Populated state
  return (
    <>
      <Card className="border-cyan-200 dark:border-cyan-800" data-testid="case-network-section">
        {cardHeader}

        <CardContent className="pt-4">
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <div className="space-y-3">
              {linkedCases.length > 0 && (
                <div>
                  <SectionHeader icon={Users} title="Linked Cases" count={linkedCases.length} color="blue" />
                  <div className="space-y-1.5">
                    {linkedCases.slice(0, isExpanded ? undefined : 4).map((conn) => (
                      <ConnectionItem
                        key={conn.edge_id || conn.node_id}
                        connection={conn}
                        isAdmin={isAdmin}
                        onRemove={handleRemove}
                      />
                    ))}
                  </div>
                </div>
              )}

              {vehicles.length > 0 && (
                <div>
                  <SectionHeader icon={Car} title="Shared Vehicles" count={vehicles.length} color="amber" />
                  <div className="space-y-1.5">
                    {vehicles.slice(0, isExpanded ? undefined : 2).map((conn) => (
                      <ConnectionItem key={conn.edge_id || conn.node_id}
                                      connection={conn} isAdmin={isAdmin} onRemove={handleRemove} />
                    ))}
                  </div>
                </div>
              )}

              {locations.length > 0 && (
                <div>
                  <SectionHeader icon={MapPin} title="Shared Locations" count={locations.length} color="green" />
                  <div className="space-y-1.5">
                    {locations.slice(0, isExpanded ? undefined : 2).map((conn) => (
                      <ConnectionItem key={conn.edge_id || conn.node_id}
                                      connection={conn} isAdmin={isAdmin} onRemove={handleRemove} />
                    ))}
                  </div>
                </div>
              )}

              {events.length > 0 && (
                <div>
                  <SectionHeader icon={Calendar} title="Shared Events" count={events.length} color="purple" />
                  <div className="space-y-1.5">
                    {events.slice(0, isExpanded ? undefined : 2).map((conn) => (
                      <ConnectionItem key={conn.edge_id || conn.node_id}
                                      connection={conn} isAdmin={isAdmin} onRemove={handleRemove} />
                    ))}
                  </div>
                </div>
              )}

              {isAdmin && poi.length > 0 && (
                <div>
                  <SectionHeader icon={AlertTriangle} title="Persons of Interest" count={poi.length} color="red" />
                  <div className="space-y-1.5">
                    {poi.slice(0, isExpanded ? undefined : 2).map((conn) => (
                      <ConnectionItem key={conn.edge_id || conn.node_id}
                                      connection={conn} isAdmin={isAdmin} onRemove={handleRemove} />
                    ))}
                  </div>
                  <div className="mt-1 text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Admin only — not visible to public
                  </div>
                </div>
              )}
            </div>

            {totalApproved > 8 && (
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full mt-3 text-muted-foreground hover:text-foreground">
                  {isExpanded ? (
                    <><ChevronDown className="w-4 h-4 mr-1" />Show Less</>
                  ) : (
                    <><ChevronRight className="w-4 h-4 mr-1" />Show All ({totalApproved} connections)</>
                  )}
                </Button>
              </CollapsibleTrigger>
            )}

            <CollapsibleContent />
          </Collapsible>

          {/* Inline mini-graph — deterministic SVG layout, not force-graph
              (force-graph collapses into a "purple blob" at this size and
              isn't usable; we save it for the Full Network dialog). */}
          {miniGraphData?.nodes?.length > 1 && (
            <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">
                  Mini network
                </div>
                <span className="text-[11px] text-muted-foreground">Click any node to open that case</span>
              </div>
              {miniGraphLoading ? (
                <div className="h-[240px] rounded-lg border bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <MiniCaseGraph
                  graphData={miniGraphData}
                  centerNodeId={miniGraphData?.center_node_id}
                  height={240}
                  onNodeClick={handleMiniNodeClick}
                />
              )}
            </div>
          )}

          {/* Admin link */}
          {isAdmin && (
            <div className="mt-4 pt-3 border-t flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                Manage all connections in the admin panel
              </span>
              <Link to="/admin/network">
                <Button variant="ghost" size="sm" className="text-xs h-7">
                  <Link2 className="w-3.5 h-3.5 mr-1" />
                  Manage Network
                </Button>
              </Link>
            </div>
          )}

          {/* Subtle loading hint while a remove is in flight */}
          {removingEdgeId && (
            <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Removing connection…
            </div>
          )}
        </CardContent>
      </Card>

      <AddConnectionModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        sourceCaseId={caseId}
        sourceCaseName={caseName}
        sourceCaseType={caseType}
        onSaved={handleConnectionSaved}
      />
      <BatchClusterConnectModal
        open={showClusterModal}
        onOpenChange={setShowClusterModal}
        sourceCaseId={caseId}
        sourceCaseName={caseName}
        sourceCaseType={caseType}
        onSaved={handleConnectionSaved}
      />
    </>
  );
};

export default CaseNetworkSection;
